import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ChevronLeft, Save, User, Heart } from 'lucide-react';
import { enfantsApi } from '../../utils/api';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';

function Section({ title, icon: Icon, children }) {
  return (
    <div className="card p-6">
      <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
        <Icon size={18} className="text-primary-500" />
        <h2 className="font-semibold text-gray-800">{title}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {children}
      </div>
    </div>
  );
}

function Field({ label, error, children, full }) {
  return (
    <div className={full ? 'md:col-span-2' : ''}>
      <label className="form-label">{label}</label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

export default function EnfantForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { structures, selectedStructure } = useApp();
  const { user } = useAuth();
  const canManage = ['admin', 'direction'].includes(user?.role);
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      enfant: { statut: 'en_attente', pai: false, structure_id: selectedStructure || '' },
      parent1: { lien: 'Mère' },
      parent2: {},
    },
  });

  // Sécurité : seuls admin/direction peuvent créer ou modifier une fiche enfant
  useEffect(() => {
    if (user && !canManage) navigate('/enfants', { replace: true });
  }, [user, canManage, navigate]);

  useEffect(() => {
    if (isEdit) {
      enfantsApi.getById(id)
        .then(res => {
          const data = res.data;
          const p1 = data.parents?.find(p => p.type === 'parent1') || {};
          const p2 = data.parents?.find(p => p.type === 'parent2') || {};
          reset({
            enfant: data,
            parent1: p1,
            parent2: p2,
          });
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [id, isEdit, reset]);

  const onInvalid = (errs) => {
    const manque = [];
    if (errs.enfant?.prenom) manque.push('le prénom');
    if (errs.enfant?.nom) manque.push('le nom');
    if (errs.enfant?.structure_id) manque.push('la structure');
    setFormError(manque.length
      ? `Veuillez compléter ${manque.join(', ')} avant d'enregistrer.`
      : 'Certains champs obligatoires sont manquants.');
  };

  const onSubmit = async (data) => {
    setFormError('');
    setSaving(true);
    try {
      if (isEdit) {
        await enfantsApi.update(id, data);
      } else {
        await enfantsApi.create(data);
      }
      navigate('/enfants');
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.error || err?.message || 'Erreur inconnue';
      setFormError('Erreur lors de la sauvegarde : ' + msg);
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { /* noop */ }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-gray-400">Chargement...</div>;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button type="button" onClick={() => navigate('/enfants')} className="btn-secondary">
          <ChevronLeft size={16} /> Retour
        </button>
        <h2 className="text-lg font-semibold text-gray-800">
          {isEdit ? 'Modifier l\'enfant' : 'Nouvel enfant'}
        </h2>
      </div>

      {formError && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
          {formError}
        </div>
      )}

      {/* Enfant section */}
      <Section title="Informations enfant" icon={User}>
        <Field label="Prénom *" error={errors.enfant?.prenom?.message}>
          <input {...register('enfant.prenom', { required: 'Le prénom est obligatoire' })} className="form-input" />
        </Field>
        <Field label="Nom *" error={errors.enfant?.nom?.message}>
          <input {...register('enfant.nom', { required: 'Le nom est obligatoire' })} className="form-input" />
        </Field>
        <Field label="Date de naissance">
          <input type="date" {...register('enfant.date_naissance')} className="form-input" />
        </Field>
        <Field label="Sexe">
          <select {...register('enfant.sexe')} className="form-select">
            <option value="">— Sélectionner —</option>
            <option value="M">Masculin</option>
            <option value="F">Féminin</option>
          </select>
        </Field>
        <Field label="Structure *" error={errors.enfant?.structure_id?.message}>
          <select {...register('enfant.structure_id', { required: 'Sélectionnez une structure' })} className="form-select">
            <option value="">— Sélectionner —</option>
            {structures.map(s => (
              <option key={s.id} value={s.id}>{s.nom}</option>
            ))}
          </select>
        </Field>
        <Field label="Statut">
          <select {...register('enfant.statut')} className="form-select">
            <option value="en_attente">En attente</option>
            <option value="actif">Actif</option>
            <option value="parti">Parti</option>
          </select>
        </Field>
        <Field label="Date d'entrée prévue">
          <input type="date" {...register('enfant.date_entree_prevue')} className="form-input" />
        </Field>
        <Field label="N° allocataire CAF">
          <input {...register('enfant.numero_allocataire')} className="form-input" placeholder="Numéro allocataire de la famille" />
        </Field>
        <Field label="Adresse" full>
          <input {...register('enfant.adresse')} className="form-input" placeholder="Adresse complète" />
        </Field>
        <Field label="Médecin traitant">
          <input {...register('enfant.medecin_nom')} className="form-input" placeholder="Nom du médecin" />
        </Field>
        <Field label="Téléphone médecin">
          <input {...register('enfant.medecin_tel')} className="form-input" />
        </Field>
        <Field label="Allergies" full>
          <textarea {...register('enfant.allergies')} className="form-input" rows={2} placeholder="Allergies connues..." />
        </Field>
        <Field label="Antécédents médicaux" full>
          <textarea {...register('enfant.antecedents')} className="form-input" rows={2} placeholder="Antécédents..." />
        </Field>
        <div className="md:col-span-2 flex items-center gap-3">
          <input type="checkbox" id="pai" {...register('enfant.pai')} className="w-4 h-4 accent-primary-500" />
          <label htmlFor="pai" className="text-sm font-medium text-gray-700">
            PAI (Projet d'Accueil Individualisé)
          </label>
        </div>
      </Section>

      {/* Parent 1 */}
      <Section title="Parent / Responsable légal 1" icon={Heart}>
        <Field label="Nom">
          <input {...register('parent1.nom')} className="form-input" />
        </Field>
        <Field label="Prénom">
          <input {...register('parent1.prenom')} className="form-input" />
        </Field>
        <Field label="Lien de parenté">
          <select {...register('parent1.lien')} className="form-select">
            <option value="Mère">Mère</option>
            <option value="Père">Père</option>
            <option value="Tuteur légal">Tuteur légal</option>
            <option value="Autre">Autre</option>
          </select>
        </Field>
        <Field label="Situation familiale">
          <select {...register('parent1.situation_familiale')} className="form-select">
            <option value="">—</option>
            <option value="Marié(e)">Marié(e)</option>
            <option value="Pacsé(e)">Pacsé(e)</option>
            <option value="Concubin(e)">Concubin(e)</option>
            <option value="Célibataire">Célibataire</option>
            <option value="Divorcé(e)">Divorcé(e)</option>
          </select>
        </Field>
        <Field label="Téléphone portable">
          <input {...register('parent1.telephone_portable')} className="form-input" />
        </Field>
        <Field label="Téléphone professionnel">
          <input {...register('parent1.telephone_pro')} className="form-input" />
        </Field>
        <Field label="Email" full>
          <input type="email" {...register('parent1.email')} className="form-input" />
        </Field>
        <Field label="Profession">
          <input {...register('parent1.profession')} className="form-input" />
        </Field>
        <Field label="Adresse" full>
          <input {...register('parent1.adresse')} className="form-input" />
        </Field>
      </Section>

      {/* Parent 2 */}
      <Section title="Parent / Responsable légal 2 (optionnel)" icon={Heart}>
        <Field label="Nom">
          <input {...register('parent2.nom')} className="form-input" />
        </Field>
        <Field label="Prénom">
          <input {...register('parent2.prenom')} className="form-input" />
        </Field>
        <Field label="Lien de parenté">
          <select {...register('parent2.lien')} className="form-select">
            <option value="">—</option>
            <option value="Mère">Mère</option>
            <option value="Père">Père</option>
            <option value="Tuteur légal">Tuteur légal</option>
            <option value="Autre">Autre</option>
          </select>
        </Field>
        <Field label="Situation familiale">
          <select {...register('parent2.situation_familiale')} className="form-select">
            <option value="">—</option>
            <option value="Marié(e)">Marié(e)</option>
            <option value="Pacsé(e)">Pacsé(e)</option>
            <option value="Concubin(e)">Concubin(e)</option>
            <option value="Célibataire">Célibataire</option>
          </select>
        </Field>
        <Field label="Téléphone portable">
          <input {...register('parent2.telephone_portable')} className="form-input" />
        </Field>
        <Field label="Email">
          <input type="email" {...register('parent2.email')} className="form-input" />
        </Field>
        <Field label="Profession">
          <input {...register('parent2.profession')} className="form-input" />
        </Field>
      </Section>

      {/* Submit */}
      <div className="flex items-center gap-3 pb-6">
        <button type="submit" disabled={saving} className="btn-primary">
          <Save size={16} /> {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
        <button type="button" onClick={() => navigate('/enfants')} className="btn-secondary">
          Annuler
        </button>
      </div>
    </form>
  );
}
